import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpService } from '../../../services/http.service';
import { ToastService } from '../../../services/toast.service';
import { environment } from '../../../../environments/environment';
import { PricingModel } from '../../bulk-pricing-models/bulk-pricing-models.component';

interface DialogData {
  mode: 'create' | 'edit';
  model?: PricingModel;
}

// The blank tier row is added for the user, so an empty required field is not
// their mistake yet. Only paint a field red once it has been touched or edited
// (save() touches everything, so the errors still show when they hit Create).
export class TouchedErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
  selector: 'app-bulk-pricing-model-dialog',
  templateUrl: './bulk-pricing-model-dialog.component.html',
  styleUrls: ['./bulk-pricing-model-dialog.component.scss'],
  providers: [{ provide: ErrorStateMatcher, useClass: TouchedErrorStateMatcher }]
})
export class BulkPricingModelDialogComponent implements OnInit {
  public form: FormGroup;
  public isEditing = false;
  public saving = false;
  public previewPrice = 3000;

  public readonly storeOptions = [
    { value: 'bhs', label: 'BlackHawk Supply' },
    { value: 'mws', label: 'Midwest Supply' }
  ];

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private toastService: ToastService,
    public dialogRef: MatDialogRef<BulkPricingModelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.isEditing = this.data.mode === 'edit';
    const model = this.data.model;

    this.form = this.fb.group({
      tag: [model?.tag || '', Validators.required],
      label: [model?.label || ''],
      priority: [model?.priority ?? 100, Validators.required],
      stores: [model?.stores?.length ? model.stores : ['bhs', 'mws'], Validators.required],
      active: [model?.active !== false],
      note: [model?.note || ''],
      tiers: this.fb.array([])
    });

    if (model?.tiers?.length) {
      model.tiers.forEach(tier => this.tiers.push(this.tierGroup(tier.min_amount, tier.max_amount, tier.discount_percent)));
    } else {
      this.addTier();
    }
  }

  ngOnInit(): void {
    // Form is built in the constructor so strict mode sees it assigned.
  }

  get tiers(): FormArray {
    return this.form.get('tiers') as FormArray;
  }

  addTier(): void {
    this.tiers.push(this.tierGroup(null, null, null));
  }

  removeTier(index: number): void {
    this.tiers.removeAt(index);
  }

  // Mirrors the feed: the discount is the highest-paying band a single unit's
  // price already reaches. Fall through every band and it stays full price.
  previewDiscount(): number {
    let percent = 0;

    for (const control of this.tiers.controls) {
      const min = Number(control.get('min_amount')?.value);
      const rawMax = control.get('max_amount')?.value;
      const max = rawMax === null || rawMax === undefined || rawMax === '' ? Infinity : Number(rawMax);
      const tierPercent = Number(control.get('discount_percent')?.value);

      if (!isFinite(min) || this.previewPrice < min || this.previewPrice > max) {
        continue;
      }

      if (isFinite(tierPercent) && tierPercent > percent) {
        percent = tierPercent;
      }
    }

    return percent;
  }

  previewFeedPrice(): number {
    const percent = this.previewDiscount();
    if (!(percent > 0 && percent < 100) || !(this.previewPrice > 0)) {
      return this.previewPrice;
    }

    // Matches the feed: the part-cent always rounds up, never down.
    return Math.ceil(this.previewPrice * (1 - percent / 100) * 100 - 1e-6) / 100;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const payload = {
      ...this.form.value,
      _id: this.data.model?._id
    };

    this.httpService.post(environment.API_PATH + '/pricing-model', payload).subscribe({
      next: (res: any) => {
        this.toastService.success(`Pricing model ${this.isEditing ? 'updated' : 'created'}`);
        (res?.warnings || []).forEach((warning: string) => this.toastService.warning(warning));
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.toastService.error(error.error?.message || 'Could not save the pricing model');
        this.saving = false;
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private tierGroup(min: number | null, max: number | null, percent: number | null): FormGroup {
    return this.fb.group({
      min_amount: [min, Validators.required],
      max_amount: [max],
      discount_percent: [percent, Validators.required]
    });
  }
}
